window.QuoteModal = function QuoteModal({ isOpen, onClose }) {
  const [formData, setFormData] = React.useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    industry: 'Railway Test Systems',
    projectType: 'Custom Test Rig',
    componentName: '',
    testParameters: '',
    automationLevel: 'Fully Automated (PLC / SCADA)',
    timeline: '1-3 Months',
    details: ''
  });

  const [selectedFile, setSelectedFile] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [emailError, setEmailError] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');

  React.useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setSubmitting(false);
      setEmailError('');
      setPhoneError('');
      setSelectedFile(null);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        industry: 'Railway Test Systems',
        projectType: 'Custom Test Rig',
        componentName: '',
        testParameters: '',
        automationLevel: 'Fully Automated (PLC / SCADA)',
        timeline: '1-3 Months',
        details: ''
      });
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setPhoneError('');

    if (!window.validateEmail(formData.email)) {
      setEmailError('Please enter a valid work email address');
      return;
    }

    if (!formData.phone) {
      setPhoneError('Phone number is required');
      return;
    }

    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setPhoneError('Phone number must be at least 10 digits');
      return;
    }

    setSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append('type', 'quote');
      formPayload.append('name', formData.fullName);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);
      formPayload.append('company', formData.company);
      formPayload.append('sector', formData.industry);
      formPayload.append('subject', formData.projectType);
      
      // Detailed engineering parameters
      formPayload.append('componentName', formData.componentName || '');
      formPayload.append('testParameters', formData.testParameters || '');
      formPayload.append('automationLevel', formData.automationLevel || '');
      formPayload.append('timeline', formData.timeline || '');
      formPayload.append('message', formData.details || '');

      if (selectedFile) {
        formPayload.append('file', selectedFile);
      }

      await window.apiClient.uploadFile('inquiries.php', formPayload);
    } catch (err) {
      console.warn('RFQ submission upload warning:', err);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 3500);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full border border-slate-200 overflow-hidden relative flex flex-col max-h-[92vh]">
        
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 sm:px-8 py-5 flex justify-between items-center border-b border-slate-800 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-secondary uppercase tracking-widest">
                Design Bureau & RFQ
              </span>
              <span className="h-1.5 w-1.5 rounded-full bg-orange-500"></span>
            </div>
            <h3 className="font-sans text-xl sm:text-2xl font-bold tracking-tight text-white mt-0.5">
              Request Engineering Consultation & RFQ
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 sm:p-8 overflow-y-auto custom-scrollbar flex-grow">
          {submitted ? (
            <div className="text-center py-12 space-y-4 animate-fadeIn">
              <div className="h-16 w-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <span className="material-symbols-outlined text-4xl">check_circle</span>
              </div>
              <h4 className="font-sans text-2xl font-bold text-slate-900">Engineering Consultation Requested</h4>
              <p className="font-sans text-sm text-slate-600 max-w-md mx-auto">
                Thank you, <strong>{formData.fullName}</strong>. Your technical parameters have been routed to our application engineering team. We will review your specification and get in touch shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Contact Information */}
              <div className="space-y-4">
                <span className="font-mono text-xs font-bold text-slate-500 uppercase tracking-wider block border-b border-slate-100 pb-1">
                  1. Contact Details
                </span>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rajesh Kumar"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-secondary outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Company / Organization *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Alstom / TVS Motors / DRDO"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-secondary outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-secondary outline-none"
                    />
                    {emailError && <span className="text-xs text-red-500 mt-1 block">{emailError}</span>}
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-secondary outline-none"
                    />
                    {phoneError && <span className="text-xs text-red-500 mt-1 block">{phoneError}</span>}
                  </div>
                </div>
              </div>

              {/* Engineering Parameters */}
              <div className="space-y-4 pt-2">
                <span className="font-mono text-xs font-bold text-slate-500 uppercase tracking-wider block border-b border-slate-100 pb-1">
                  2. Technical & Project Parameters
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Target Industry
                    </label>
                    <select
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none bg-white font-sans"
                    >
                      <option value="Railway Test Systems">Railway Test Systems</option>
                      <option value="Automotive Test & Automation">Automotive Test & Automation</option>
                      <option value="Aerospace & Defence">Aerospace & Defence</option>
                      <option value="Industrial Automation & SPM">Industrial Automation & SPM</option>
                      <option value="R&D & Academic Research">R&D & Academic Research</option>
                      <option value="Other">Other Application</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Required System Type
                    </label>
                    <select
                      value={formData.projectType}
                      onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none bg-white font-sans"
                    >
                      <option value="Custom Test Rig">Custom Test Rig</option>
                      <option value="Automated Test Bench">Automated Test Bench</option>
                      <option value="Robotic Automation Cell">Robotic Automation Cell</option>
                      <option value="Special Purpose Machine (SPM)">Special Purpose Machine (SPM)</option>
                      <option value="End-of-Line (EOL) Validation Station">End-of-Line (EOL) Validation Station</option>
                      <option value="DAQ & Software System">DAQ & Software System</option>
                      <option value="Other">Other Custom Machinery</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Product / Component to Test
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Brake Valve, Shock Absorber, Actuator"
                      value={formData.componentName}
                      onChange={(e) => setFormData({ ...formData, componentName: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Test Parameters (Pressure, Force, Flow, Cycles)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Up to 10 Bar, 50 kN force, 100k cycles"
                      value={formData.testParameters}
                      onChange={(e) => setFormData({ ...formData, testParameters: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Automation Level
                    </label>
                    <select
                      value={formData.automationLevel}
                      onChange={(e) => setFormData({ ...formData, automationLevel: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none bg-white"
                    >
                      <option value="Fully Automated (PLC / SCADA)">Fully Automated (PLC / SCADA)</option>
                      <option value="Semi-Automatic with Manual Load">Semi-Automatic with Manual Load</option>
                      <option value="Manual Test Station with Digital DAQ">Manual Test Station with Digital DAQ</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Required Project Delivery Timeline
                    </label>
                    <select
                      value={formData.timeline}
                      onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none bg-white"
                    >
                      <option value="Immediate / RFQ Stage">Immediate / RFQ Stage</option>
                      <option value="1-3 Months">1-3 Months</option>
                      <option value="3-6 Months">3-6 Months</option>
                      <option value="Budgeting / Feasibility Study">Budgeting / Feasibility Study</option>
                    </select>
                  </div>
                </div>

                {/* File Upload for RFQ / Specification / Drawing */}
                <div>
                  <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                    Upload Specification / Drawing / RFQ Document (PDF, STEP, CAD, DOCX)
                  </label>
                  <div className="border-2 border-dashed border-slate-300 hover:border-secondary rounded-xl p-4 text-center cursor-pointer transition-colors bg-slate-50 relative">
                    <input
                      type="file"
                      accept=".pdf,.step,.stp,.dwg,.dxf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    <div className="flex flex-col items-center gap-1 text-slate-500">
                      <span className="material-symbols-outlined text-2xl text-secondary">upload_file</span>
                      <span className="text-xs font-mono font-bold text-slate-700">
                        {selectedFile ? `Selected: ${selectedFile.name}` : 'Click or Drag & Drop Drawing / Specification PDF'}
                      </span>
                      <span className="text-[10px] text-slate-400">Max size 25MB • Confidential NDA Protected</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                    Additional Scope & Acceptance Criteria
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Describe testing sequences, standard tolerances (RDSO, UIC, ISO), cycle times, or specific DAQ requirements..."
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                  ></textarea>
                </div>

              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-slate-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 text-xs font-mono font-bold text-slate-600 hover:text-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-secondary hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-bold text-sm shadow-md hover:shadow-orange-500/20 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                >
                  <span className="material-symbols-outlined text-lg">send</span>
                  <span>{submitting ? 'Submitting Specifications...' : 'Submit Engineering RFQ'}</span>
                </button>
              </div>

            </form>
          )}
        </div>

      </div>
    </div>
  );
};
